import { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { SessionFeedbackModal } from "@/components/feedback/SessionFeedbackModal";
import { generateUUID } from "@/lib/uuid";

interface FeedbackContextValue {
  triggerFeedback: (options?: {
    pagePath?: string;
    courseId?: string;
    chapterId?: string;
  }) => void;
}

const FeedbackContext = createContext<FeedbackContextValue | null>(null);

export function useFeedbackContext() {
  const ctx = useContext(FeedbackContext);
  if (!ctx) {
    // Return a no-op if not wrapped in provider (graceful degradation)
    return { triggerFeedback: () => {} };
  }
  return ctx;
}

function getSessionId(): string {
  try {
    let sessionId = sessionStorage.getItem("mivobox:session_id");
    if (!sessionId) {
      sessionId = generateUUID();
      sessionStorage.setItem("mivobox:session_id", sessionId);
    }
    return sessionId;
  } catch {
    return generateUUID();
  }
}

interface FeedbackProviderProps {
  children: ReactNode;
}

export function FeedbackProvider({ children }: FeedbackProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [feedbackMeta, setFeedbackMeta] = useState<{
    pagePath?: string;
    courseId?: string;
    chapterId?: string;
  }>({});

  const triggerFeedback = useCallback((options?: {
    pagePath?: string;
    courseId?: string;
    chapterId?: string;
  }) => {
    // Check if already shown this session (shared key with useSessionFeedbackTrigger)
    const storageKey = "mivobox:session_feedback_shown";
    try {
      if (sessionStorage.getItem(storageKey)) {
        return;
      }
      // Mark as shown to prevent future triggers
      sessionStorage.setItem(storageKey, "1");
    } catch {
      // ignore storage errors
    }

    setFeedbackMeta({
      pagePath: options?.pagePath || window.location.pathname,
      courseId: options?.courseId,
      chapterId: options?.chapterId,
    });
    setIsOpen(true);
  }, []);

  return (
    <FeedbackContext.Provider value={{ triggerFeedback }}>
      {children}
      <SessionFeedbackModal
        open={isOpen}
        onOpenChange={setIsOpen}
        sessionId={getSessionId()}
        pagePath={feedbackMeta.pagePath}
        courseId={feedbackMeta.courseId}
        chapterId={feedbackMeta.chapterId}
      />
    </FeedbackContext.Provider>
  );
}
